import { baseApi } from "./baseApi";
import { PaginationArgs } from "types/globalTypes";
import { buildQueryParams } from "services/helper";
import {
  AdminDashboardOverview,
  AdminDashboardReport,
  AdminRankingOhi,
  FormsResponseType,
  FormsType,
} from "types/reportTypes";

export const reportAndFormApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // dashboard
    getAdminDashboardOverview: builder.query<AdminDashboardOverview, string>({
      query: (scope) => `/${scope}/dashboard/overview`,
      providesTags: ["AdminDashboardOverview"],
    }),

    getAdminAverageOhi: builder.query<
      AdminDashboardReport,
      { fromDate: string; toDate: string; scope: string }
    >({
      query: ({ fromDate, toDate, scope }) => ({
        url: `/${scope}/dashboard/average/ohi?fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
      providesTags: ["AdminAverageOhi"],
    }),

    getAdminRankingOhi: builder.query<
      AdminRankingOhi[],
      {
        industrialSectorId?: number;
        fromDate: string;
        toDate: string;
        mode: "top" | "bottom";
        scope: string;
      }
    >({
      query: ({ industrialSectorId, fromDate, toDate, mode, scope }) => ({
        url: `/${scope}/dashboard/ranking/ohi?fromDate=${fromDate}&toDate=${toDate}&mode=${mode}${
          industrialSectorId ? `&industrialSectorId=${industrialSectorId}` : ""
        }`,
        method: "GET",
      }),
      providesTags: ["AdminRankingOhi"],
    }),

    getAdminAssessmentSales: builder.query<
      AdminDashboardReport,
      { fromDate: string; toDate: string; scope: string }
    >({
      query: ({ fromDate, toDate, scope }) => ({
        url: `/${scope}/dashboard/sales?fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
      providesTags: ["AdminAssessmentSales"],
    }),

    getAdminAssessmentExport: builder.query<
      AdminDashboardReport,
      { fromDate: string; toDate: string; scope: string }
    >({
      query: ({ fromDate, toDate, scope }) => ({
        url: `/${scope}/dashboard/export?fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
      providesTags: ["AdminAssessmentExport"],
    }),

    getAdminAssessmentCapacityUtilization: builder.query<
      AdminDashboardReport,
      { fromDate: string; toDate: string; scope: string }
    >({
      query: ({ fromDate, toDate, scope }) => ({
        url: `/${scope}/dashboard/capacity-utilization?fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
      providesTags: ["AdminAssessmentCapacityUtilization"],
    }),

    getAdminAssessmentTechnologicSales: builder.query<
      AdminDashboardReport,
      { fromDate: string; toDate: string; scope: string }
    >({
      query: ({ fromDate, toDate, scope }) => ({
        url: `/${scope}/dashboard/sales/knowledge-and-technologic?fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
      providesTags: ["AdminAssessmentTechnologicSales"],
    }),

    // admin / organization forms
    getForms: builder.query<FormsResponseType, PaginationArgs>({
      query: (args) => `/form?${buildQueryParams(args || {})}`,
      providesTags: ["Forms"],
    }),

    getFormById: builder.query<FormsType, number>({
      query: (id) => `/form/${id}`,
      providesTags: ["Forms"],
    }),

    createForm: builder.mutation<FormsType, Partial<FormsType>>({
      query: (newData) => ({
        url: "/form",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Forms"],
    }),

    updateForm: builder.mutation<
      FormsType,
      { id: number; data: Partial<FormsType> }
    >({
      query: ({ id, data }) => ({
        url: `/form/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Forms"],
    }),

    deleteForm: builder.mutation<void, number>({
      query: (id) => ({
        url: `/form/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Forms"],
    }),
  }),
});

export const {
  // admin
  useGetAdminDashboardOverviewQuery,
  useGetAdminAverageOhiQuery,
  useGetAdminRankingOhiQuery,
  useGetAdminAssessmentSalesQuery,
  useGetAdminAssessmentExportQuery,
  useGetAdminAssessmentCapacityUtilizationQuery,
  useGetAdminAssessmentTechnologicSalesQuery,
  // admin / organization forms
  useGetFormsQuery,
  useLazyGetFormsQuery,
  useGetFormByIdQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
} = reportAndFormApi;
